import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageList } from 'react-chat-elements';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import moment from 'moment';
import { getAllMessagesAPI, markReadChatMessagesAPI } from '../../../utils/apiServices/chatsAPI';
import {
    initializeMessages,
    prependMessages,
    resetChatNotification,
    setCurrentView,
    setSelectedUser,
} from '../../../redux';
import { getCustomerByID, getLocalStorageItem, popup } from '../../../utils';
import { LOCAL_STORAGE } from '../../../constants';
import {
    CURRENT_VIEW,
    formattedMessageDate,
    MESSAGE_ADD_TYPE,
} from '../../shared/FloatingChatElement/constant';
import { useDebounce } from '../../../hooks';
import { ChatSearch } from '../../shared/FloatingChatElement/ChatSearch';

const ChatBody = () => {
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const isImpersonating = getLocalStorageItem(LOCAL_STORAGE.IS_IMPERSONATING);
    const selectedAccount = useSelector((s) => s.accountArray?.selectedAccount);
    const accounts = useSelector((s) => s.accountArray?.accounts);
    const messages = useSelector((s) => s.chat.messages);
    const chatList = useSelector((s) => s.chat.chatList?.[0]);
    const msgAddType = useSelector((s) => s.chat.messageAddType);

    const [isFetching, setIsFetching] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [search, setSearch] = useState('');

    const getAllMessages = useDebounce(async ({ before, last_message_id, prepend = false }) => {
        if (!chatList?.chat_id || isFetching || (prepend && !hasMore)) return;
        setIsFetching(true);
        try {
            const { data, count } = await getAllMessagesAPI({
                chat_id: chatList?.chat_id,
                before,
                last_message_id,
                search,
            });
            if (prepend && messages?.length > 0) {
                dispatch(prependMessages(data));
            } else {
                dispatch(initializeMessages(data));
            }

            if (messages?.length + data?.length >= count || data.length === 0) {
                setHasMore(false);
            }
        } catch (error) {
            popup('error', error.message || 'Failed to fetch messages');
        } finally {
            setIsFetching(false);
            dispatch(setCurrentView(CURRENT_VIEW.MESSAGE_VIEW));
        }
    }, 500);

    const markMessagesAsRead = async () => {
        if (!chatList?.chat_id) return;
        await markReadChatMessagesAPI({ chat_id: chatList.chat_id });
    };

    const fetchAccountCSM = useCallback(async () => {
        try {
            const { data } = await getCustomerByID({
                customerID: selectedAccount,
                fetchPlatform: false,
                fetchHiringClient: false,
                fetchCSM: true,
            });
            dispatch(setSelectedUser(data?.[0]?.csm?.[0]));
        } catch (error) {
            popup('error', error.message || 'Failed to fetch messages');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount]);

    useEffect(() => {
        if (selectedAccount && chatList?.chat_id && accounts?.length > 0) {
            getAllMessages({ prepend: false });
        }
        return () => {
            if (!isImpersonating) markMessagesAsRead();
            dispatch(resetChatNotification());
            dispatch(initializeMessages([]));
            setHasMore(true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount, chatList?.chat_id, search]);

    useEffect(() => {
        if (selectedAccount) {
            fetchAccountCSM();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount]);

    useEffect(() => {
        if (scrollRef.current && msgAddType === MESSAGE_ADD_TYPE.APPEND) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                // behavior: 'smooth',
            });
        }
    }, [messages?.length, msgAddType]);

    const handleScroll = async () => {
        const el = scrollRef.current;
        if (!el || el.scrollTop > 50 || isFetching || !hasMore || messages.length < 50) return;

        const firstMsg = messages[0];
        if (!firstMsg) return;

        // const before = firstMsg.date;
        const before = moment(firstMsg.date).format('YYYY-MM-DD HH:mm:ss');
        const last_message_id = firstMsg.id;
        const prevScrollHeight = el.scrollHeight;

        await getAllMessages({ before, prepend: true, last_message_id });

        requestAnimationFrame(() => {
            const newScrollHeight = el.scrollHeight;
            el.scrollTop = newScrollHeight - prevScrollHeight;
        });
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages?.length, isFetching, hasMore]);

    return (
        <>
            <ChatSearch search={search} setSearch={setSearch} placeholder='Search Message...' />
            <div className='chat-body message-body' ref={scrollRef}>
                {isFetching && messages?.length > 0 && (
                    <span className='d-flex justify-content-center'>
                        <Spinner />
                    </span>
                )}
                <MessageList
                    className='message-list'
                    lockable={true}
                    toBottomHeight='100%'
                    dataSource={messages.map((msg) => ({
                        ...msg,
                        title: msg.position === 'right' ? 'You' : msg.title,
                        dateString: formattedMessageDate(msg.date),
                    }))}
                />
            </div>
        </>
    );
};

export default ChatBody;
